<template>
  <div class="personalize">
    <div class="personalize__image">
      <label class="personalize__label" for="input-image-upload">{{ t('settings.imageLabel') }}</label>

      <div class="personalize__image-upload">
        <input
          id="input-image-upload"
          class="personalize__image-upload-input"
          type="file"
          name="image"
          accept="image/png, image/jpeg, image/jpg, image/gif"
          ref="input-image"
          @input="onImageSelected($event)"
        >
        <IconImage v-if="!hasImage" :extra-classes="['.personalize__image-upload-preview', 'personalize__image-upload-dummy']" />
        <IconSpinner v-if="isImageLoading" :extra-classes="['spinner']" />

        <img
          v-show="hasImage"
          :class="{
            'personalize__image-upload-preview': true,
            'personalize__image-upload-preview--loaded': imagePreviewLoaded,
          }"
          :src="imagePreviewUrl!"
          :alt="t('settings.imageAlt', { '@name': nickName! })"
          @load="onImageLoaded(true)"
          @error="onImageLoaded(false)"
        >

        <div :class="{
          'personalize__image-actions': true,
          'personalize__image-actions--visible': hasImage,
        }">
          <a
            v-if="hasImage"
            class="personalize__image-action personalize__image-action--delete"
            @click="removeImage()"
          >
            <IconTrash />
          </a>
        </div>
      </div>

      <div class="personalize__image-title">
        {{ nickName }}
      </div>
    </div>
    <div class="personalize__description">

      <!-- description -->
      <label class="personalize__label" for="text-descriptionUserValue">
        {{ t('settings.publicCubeDescriptionLabel')}}
      </label>
      <textarea
        id="text-descriptionUserValue"
        v-model="descriptionUserValue"
        class="personalize__description-textarea"
      ></textarea>

      <br />
      <br />

      <!-- public phone number-->
      <label class="personalize__label" for="input-publicContactPhoneNumber">
        {{ t('settings.publicContactPhoneNumberLabel')}}
      </label>
      <FormPhoneInput
        id="input-publicContactPhoneNumber"
        :placeholder="t('settings.publicContactPhoneNumber')"
        v-model="publicContactPhoneNumber.value"
        @valid="onPublicContactPhoneNumberChanged($event, true)"
        @invalid="onPublicContactPhoneNumberChanged($event, false)"/>
      <p class="personalize__description-long">
        <UnicodeIcon :bold="true" size="" color="black" icon="ⓘ" />
        {{ t("settings.publicContactPhoneNumberDescription") }}
      </p>
      <p class="personalize__description-long">
        {{ t("settings.publicContactPhoneNumberDisclaimer") }}
      </p>

      <br />
      <br />

    </div>

  </div>

  <LinkButton
    :disabled="!isSaveButtonEnabled"
    icon-right="Arrow"
    form="cubePersonalization"
    :text="t('global.save')"
    @click="saveChanges()"
    :extra-classes="['personalize__save']"
  />

  <LinkButton
    :text="t('global.changePassword')"
    @click="showResetPasswordForm = !showResetPasswordForm"
    :extra-classes="['toggle-pw-reset button--secondary']"
  />

  <div :class="resetPasswordClasses()">
    <p v-if="changePasswordFinished()">
      {{ formsStore.getFormMessage('changePassword') }}
    </p>
    <p v-else>
      Wir senden ein E-Mail an "<em>{{ contractEmail }}</em
      >", das einen Link zum Ändern deines Passworts enthält.
    </p>
    <LinkButton
      v-if="!changePasswordFinished()"
      :text="t('global.changePassword')"
      :icon-right="changePasswordSubmitIcon"
      form="changePassword"
      :disabled="changePasswordFinished()"
      :extra-classes="changePasswordButtonClasses()"
      @click="authStore.startChangePassword()"
    />
  </div>
</template>

<script setup lang="ts">
  import IconSpinner from '@/components/icons/Spinner.vue'
  import IconTrash from '@/components/icons/Trash.vue'
  import IconImage from '@/components/icons/Image.vue'
  import LinkButton from '@/components/LinkButton.vue'
  import { useStrings } from '@/composables/useStrings.ts'
  import { useCubeStore } from '@/stores/useCubeStore'
  import { useFormsStore } from '@/stores/useFormsStore'
  import { computed, ref, useTemplateRef, watch, type Ref } from 'vue'
  import FormPhoneInput from '@/components/forms/FormPhoneInput.vue'
  import { useDirtyRef } from '@/composables/useDirtyRef'
  import { useAuthStore } from '@/stores/useAuthStore'
  import { useSettingsStore } from '@/stores/useSettingsStore'
  import { useNotificationsStore } from '@/stores/useNotificationsStore'
  import UnicodeIcon from '@/components/icons/UnicodeIcon.vue'

  const { t } = useStrings()
  const cubeStore = useCubeStore()
  const formsStore = useFormsStore()
  const authStore = useAuthStore()
  const settingsStore = useSettingsStore()
  const notifications = useNotificationsStore()

  const nickName = computed(() => cubeStore.nickName)
  const inputImageRef: Ref<HTMLInputElement | null> = useTemplateRef('input-image')
  const descriptionUserValue = ref(cubeStore.descriptionText)
  const publicContactPhoneNumber = useDirtyRef(cubeStore.publicContactPhoneNumber)
  const publicContactPhoneNumberValid = ref(false)

  const imagePreviewUrl: Ref<string | null> = ref(null)
  const imagePreviewLoaded = ref(false)
  const hasImage = computed(() => !!imagePreviewUrl.value)
  const storedImageUrl = computed(() => {
    const imgs = cubeStore.images as any
    return imgs && Object.keys(imgs).length > 0 ? imgs.original?.url : false
  })

  const contractEmail = computed(() => settingsStore.contractData.email)
  const showResetPasswordForm = ref(false)

  const changePasswordFinished = () => formsStore.getFormState('changePassword') === 'finished'
  const changePasswordSubmitIcon = computed(() => changePasswordFinished() ? 'Check' : 'Arrow')

  const resetPasswordClasses = () => {
    const classes = ['pw-reset']

    if (showResetPasswordForm.value) {
      classes.push('pw-reset--visible')
    }

    return classes.join(' ')
  }

  const changePasswordButtonClasses = () => {
    return changePasswordFinished() ? ['button--success'] : []
  }

  // Set the preview image's url in case the data is available already.
  imagePreviewUrl.value = storedImageUrl.value
  watch(storedImageUrl, (newValue) => {
    // Set the preview image's url as soon as the data is available.
    // This will be necessary if the user directly navigates to the url of the
    // settings page.
    imagePreviewUrl.value = newValue
  })

  const onImageLoaded = async(success: boolean) => {
    imagePreviewLoaded.value = true
  };

  const descriptionChanged = computed(() => {
    return cubeStore.descriptionText !== descriptionUserValue.value
  })

  const isImageLoading = computed(() => {
    return hasImage.value && !imagePreviewLoaded.value
  })

  const isSaveButtonEnabled = computed(() => {
    return (!descriptionChanged.value && publicContactPhoneNumber.isDirty.value && publicContactPhoneNumberValid.value) ||
      (descriptionChanged.value && (!publicContactPhoneNumber.isDirty.value)) ||
      (descriptionChanged.value && publicContactPhoneNumber.isDirty.value && publicContactPhoneNumberValid.value)
  })

  const onPublicContactPhoneNumberChanged = (phoneNumber: string, isValid = false) => {
    publicContactPhoneNumberValid.value = isValid;
  }

  const onImageSelected = async (event: InputEvent) => {
    const file = inputImageRef?.value?.files?.[0];

    if (file) {
      const newImageUrl = URL.createObjectURL(file)
      // if there is no image yet, we want to immediately set the new one
      if (!imagePreviewUrl.value) {
        imagePreviewUrl.value = newImageUrl
      }
      imagePreviewLoaded.value = false;

      await cubeStore.updateCubeData({
        image: inputImageRef.value.files?.[0],
        imagePreviewUrl: newImageUrl,
      })

      notifications.showNotification({body: t("global.myDataSavedSuccessfully")});

      imagePreviewLoaded.value = true;
    } else {
      console.debug("No profile image selected");
    }
  }

  const removeImage = async() => {
    imagePreviewLoaded.value = false;
    inputImageRef.value.value = null
    imagePreviewUrl.value = null

    await cubeStore.deleteCubeImage();

    notifications.showNotification({body: t("global.myDataSavedSuccessfully")});
  }

  const saveChanges = async () => {
    try {
      formsStore.setFormState('cubePersonalization', 'submitting')

      await cubeStore.updateCubeData({
        description: descriptionUserValue.value,
        publicContactPhoneNumber: publicContactPhoneNumber.value || undefined
      })

      formsStore.setFormState('cubePersonalization', false)

      publicContactPhoneNumber.resetDirtyState();

      notifications.showNotification({body: t("global.myDataSavedSuccessfully")});
    } catch (e) {
      return false
    }

    return true;
  }

</script>

<style lang="scss">
$image-actions-width: $spacer30;
$image-actions-margin-left: $spacer05;

.personalize {
  margin-bottom: $spacer30;

  @media (min-width: $bp-sm) {
    display: flex;
  }

  .sk-fading-circle {
    z-index: 9999;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }

  &__label {
    display: block;
    width: 100%;
  }

  &__description-long {
    margin-top: 10px;
    margin-bottom: 10px;
    font-size: medium;
  }

  &__image {
    // display: flex;
    // flex-wrap: wrap;
    // flex-shrink: 2;
    position: relative;

    &-upload {
      width: 200px;
      height: 250px;
      position: relative;
      // To make the image action be hidden behind the upload area, when no
      // image is selected.
      background: $white;
      z-index: 1;
      border: 2px solid $grey-lighter;
      border-radius: $border-radius;
      margin-left: auto;
      margin-right: auto;

      &::before {
        content: "";
        position: absolute;
        left: 0;
        top: 0;
        right: 0;
        bottom: 0;
      }

      .icon--spinner {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        animation: spin-centered 1.8s linear 0s infinite;
        width: 2rem;
      }

      &-dummy {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        width: 5rem;
        opacity: 0.2;
        z-index: 1;

      }

      &-input {
        position: absolute;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        display: block;
        opacity: 0;
        padding: $spacer05;
        cursor: pointer;
        z-index: 2;
        border: 2px solid $grey-lighter;
      }

      &-preview {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        z-index: 1;
        object-fit: cover;
        border-radius: $border-radius;
        // Hide the image by default and show it after it loaded.
        opacity: 0;
        transition: all 0.3s ease 0s;

        &--loaded {
          opacity: 1;
        }

      }
    }

    &-actions {
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
      width: $image-actions-width;
      transform: translateX(-150%);
      transition: $transition-base;
      bottom: 10px;
      right: 8px;
      position: absolute;

      &--visible {
        transform: translateX(0);
        z-index: 10;

        a {
          background-color: #FFFFFFCC;
          border-radius: 20px;
        }
      }
    }

    &-action {
      cursor: pointer;
      display: inline-block;
      padding: 0.25em;
      color: $grey-dark;
      transition: $transition-base;
      text-align: center;

      &:hover,
      &:focus,
      &:active {
        color: $grey-darker;
      }

      &:last-child {
        margin-top: $spacer025;
      }

      .icon {
        max-width: 100%;
        vertical-align: text-bottom;
      }

      &--delete {
      }
      &--rotate {
        margin-top: $spacer025;
        margin-bottom: auto;
      }
    }

    &-title {
      // Must equal imageWidth to align the title properly.
      width: 100%;
      margin-right: $image-actions-width + $image-actions-margin-left;
      text-align: center;
      font-weight: bold;
      margin-top: $spacer075;
      font-size: $font-size-md;
    }
  }

  &__description {
    flex-grow: 1;
    width: 100%;
    margin-top: $spacer20;

    @media (min-width: $bp-sm) {
      margin-left: $spacer20;
      margin-top: 0;
    }

    &-textarea {
      min-height: 14em;
    }
  }

  &__save {
    margin-top: $spacer10;
    float: right;
  }

}

.toggle-pw-reset {
  cursor: pointer;
  margin-top: $spacer10;
}

.pw-reset {
  max-height: 0;
  overflow: hidden;
  transition: $transition-base;
  margin-top: $spacer10;
  margin-bottom: $spacer40;

  em {
    font-style: normal;
    font-weight: 600;
  }

  &--visible {
    max-height: 200px;
  }
}
</style>
